import { FormControl, FormHelperText, InputLabel, Select, MenuItem, ListItemText, Box, Chip, Checkbox } from '@mui/material'
import PropTypes from 'prop-types'
import { Controller } from 'react-hook-form'

/**
 * @description Renders a multiple select form component.
 *
 * @author Maria Eduarda Franke
 * @param {string} props.name - The name of the select field.
 * @param {string} props.label - The label for the select field.
 * @param {Array} props.items - The array of items to be displayed in the select field.
 * @param {string} props.itemId - The key of the item to be used as the value in the select field.
 * @param {Object} props.control - The control object from react-hook-form.
 * @param {Object} props.errors - The errors object from react-hook-form.
 * @returns {JSX.Element} - The rendered multiple select form component.
 */
const FormMultipleSelect = ({ name, label, items, itemId, control, errors, ...props }) => {

    const getItemName = (value) => {
        const item = items.find((item) => item[itemId] === value)
        return item ? item.name : ''
    }

    const renderSelect = (field) => (
        <Select
            {...field}
            label={label}
            multiple
            renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1, mb: 1 }}>
                    {selected.map((value) => (
                        <Chip key={value} label={getItemName(value)} color='secondary' />
                    ))}
                </Box>
            )}
        >
            {items.map((item) => (
                <MenuItem key={item[itemId]} value={item[itemId]}>
                    <Checkbox checked={field.value.includes(item[itemId])} />
                    <ListItemText primary={item.name} />
                </MenuItem>
            ))}
        </Select>
    )

    return (
        <Controller
            control={control}
            name={name}
            render={({ field }) => (
                <FormControl
                    error={!!errors}
                    {...props}
                >
                    <InputLabel>{label}</InputLabel>
                    {renderSelect(field)}
                    <FormHelperText error={!!errors}>{errors?.message}</FormHelperText>
                </FormControl>
            )}
        />
    )
}

FormMultipleSelect.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    items: PropTypes.array.isRequired,
    itemId: PropTypes.string.isRequired,
    control: PropTypes.object,
    errors: PropTypes.object
}

export default FormMultipleSelect